import React from "react";
import { Badge, Box, Button, Container, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

/**
 * The one header used by every directory index page, in whichever of the
 * three looks the site already ships. Which look a page wears is an admin
 * setting (Visibility → Index Page Styles), so this component reproduces
 * all three faithfully rather than each page owning its own markup.
 *
 *  1 "directory"  Plain page wash, "{X} Directory" title with the action
 *                 buttons on the SAME row, uppercase secondary subtitle
 *                 beneath, then the featured banner, then the filter bar.
 *                 (Organizations, Professionals today.)
 *
 *  2 "panel"      Accent-tinted page background behind a white content
 *                 container; accent-coloured heading and create button.
 *                 (Articles, Regulations today.)
 *
 *  3 "banner"     Full-bleed solid-accent hero with WHITE heading and
 *                 subtitle, create controls parked bottom-right. The hero
 *                 is replaced by the featured banner when a promotion is
 *                 live, so `hasFeatured` must be passed or admins lose the
 *                 create button whenever a promo runs. (Events today.)
 */

export type IndexStyle = 1 | 2 | 3;

export interface IndexHeaderAction {
    label: string;
    onClick: () => void;
    /** Rendered only for admins (e.g. CSV/table upload). */
    adminOnly?: boolean;
    /** Optional count badge on the primary action. */
    badge?: { count: number; color: "info" | "error" } | null;
}

export interface IndexPageHeaderProps {
    style: IndexStyle;
    accent: { primary: string; dark: string };
    title: string;
    subtitle?: string;
    /** aria-label / data-select-target, e.g. "Organizations Directory Header". */
    ariaLabel: string;
    isAdmin?: boolean;
    /** Primary CTA. Omit to hide (admin `showCreate: false`). */
    create?: IndexHeaderAction;
    /** CSV / table upload. Omit when the page has no upload handler wired —
     *  the admin toggle grants permission, it cannot invent an importer. */
    upload?: IndexHeaderAction;
    /** Small "N submissions pending" text button under the CTA. */
    status?: { label: string; onClick: () => void };
    /**
     * Style 3 only: true when a featured promotion is live, in which case
     * the hero is suppressed (the banner takes its place) and the actions
     * render inline instead of over the hero.
     */
    hasFeatured?: boolean;
    /**
     * Style 3 only: how far the hero bleeds.
     * - "viewport" (default): the 100vw negative-margin trick. Correct when
     *   the header sits in a viewport-centered column (organizations,
     *   events, professionals render it outside their containers).
     * - "container": cancel the parent container's spacing(4) side padding
     *   only. Required inside the articles/regulations shell, whose fixed
     *   side nav offsets the column — viewport math there shifts the hero
     *   right by half the gutter and clips it (and a true viewport-wide
     *   hero would run under the side nav anyway).
     */
    bleed?: "viewport" | "container";
}

/** The action cluster, shared by all three styles; `onHero` flips it to
 *  the white-on-accent treatment used inside the style-3 hero. */
function Actions({
    accent,
    isAdmin,
    create,
    upload,
    status,
    onHero = false,
}: Pick<
    IndexPageHeaderProps,
    "accent" | "isAdmin" | "create" | "upload" | "status"
> & { onHero?: boolean }) {
    if (!create && !upload && !status) return null;

    const primarySx = onHero
        ? {
              backgroundColor: "#fff",
              color: accent.dark,
              "&:hover": { backgroundColor: alpha("#fff", 0.88) },
          }
        : {
              backgroundColor: accent.primary,
              color: "#fff",
              "&:hover": { backgroundColor: accent.dark },
          };

    const outlinedSx = onHero
        ? {
              borderColor: "#fff",
              color: "#fff",
              "&:hover": { borderColor: "#fff", backgroundColor: alpha("#fff", 0.12) },
          }
        : {
              borderColor: accent.primary,
              color: accent.primary,
              backgroundColor: "#fff",
              "&:hover": { borderColor: accent.dark, backgroundColor: alpha(accent.primary, 0.08) },
          };

    const primaryButton = create ? (
        <Button
            variant="contained"
            size="large"
            onClick={create.onClick}
            sx={{ whiteSpace: "nowrap", ...primarySx }}
        >
            {create.label}
        </Button>
    ) : null;

    return (
        <Box
            sx={{
                flex: "0 0 auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 1,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {upload && (!upload.adminOnly || isAdmin) && (
                    <Button
                        variant="outlined"
                        onClick={upload.onClick}
                        // Hidden on small screens: the row needs the width
                        // for the title and the primary CTA.
                        sx={{
                            whiteSpace: "nowrap",
                            display: { xs: "none", md: "inline-flex" },
                            ...outlinedSx,
                        }}
                    >
                        {upload.label}
                    </Button>
                )}
                {primaryButton && create?.badge ? (
                    <Badge
                        badgeContent={create.badge.count}
                        color={create.badge.color}
                        invisible={!create.badge.count}
                    >
                        {primaryButton}
                    </Badge>
                ) : (
                    primaryButton
                )}
            </Box>
            {status && (
                <Button
                    size="small"
                    onClick={status.onClick}
                    sx={{
                        textTransform: "none",
                        color: onHero ? "#fff" : "text.secondary",
                    }}
                >
                    {status.label}
                </Button>
            )}
        </Box>
    );
}

export default function IndexPageHeader({
    style,
    accent,
    title,
    subtitle,
    ariaLabel,
    isAdmin = false,
    create,
    upload,
    status,
    hasFeatured = false,
    bleed = "viewport",
}: IndexPageHeaderProps) {
    const actions = (
        <Actions
            accent={accent}
            isAdmin={isAdmin}
            create={create}
            upload={upload}
            status={status}
        />
    );

    // ── Style 3: solid-accent hero, white type, actions bottom-right ──
    if (style === 3) {
        if (hasFeatured) {
            // The featured banner replaces the hero — surface the actions
            // inline so create never disappears while a promo is live.
            return (
                <Box
                    aria-label={ariaLabel}
                    data-select-target={ariaLabel}
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mb: 2,
                    }}
                >
                    {actions}
                </Box>
            );
        }
        return (
            <Box
                aria-label={ariaLabel}
                data-select-target={ariaLabel}
                sx={{
                    backgroundColor: accent.primary,
                    color: "#fff",
                    // Break out of the parent container so the hero doesn't
                    // render as a boxed accent card. See `bleed` docs: the
                    // viewport trick mis-centers inside the side-nav-gutted
                    // shell, so shell pages cancel the container padding
                    // instead and span their content column.
                    ...(bleed === "viewport"
                        ? {
                              width: "100vw",
                              maxWidth: "100vw",
                              position: "relative" as const,
                              left: "50%",
                              right: "50%",
                              marginLeft: "-50vw",
                              marginRight: "-50vw",
                          }
                        : {
                              width: "auto",
                              position: "relative" as const,
                              mx: -4,
                          }),
                    // Flush to the navbar above; space the content below.
                    mb: { xs: 3, md: 4 },
                    py: 8,
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ p: 4 }}>
                        <Typography variant="h2" component="h1" gutterBottom>
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography variant="h5" component="p">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </Container>
                <Box
                    sx={{
                        position: "absolute",
                        bottom: { xs: 12, md: 16 },
                        right: { xs: 12, md: 16 },
                    }}
                >
                    <Actions
                        accent={accent}
                        isAdmin={isAdmin}
                        create={create}
                        upload={upload}
                        status={status}
                        onHero
                    />
                </Box>
            </Box>
        );
    }

    // ── Style 2: accent-coloured heading over the white panel ──
    if (style === 2) {
        return (
            <Box
                aria-label={ariaLabel}
                data-select-target={ariaLabel}
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontWeight: 700, color: accent.primary }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {actions}
            </Box>
        );
    }

    // ── Style 1: plain directory header, actions on the title row ──
    return (
        <Box
            aria-label={ariaLabel}
            data-select-target={ariaLabel}
            sx={{ mb: 6, pt: 4 }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Typography
                    variant="h2"
                    component="h1"
                    sx={{ m: 0, flex: "1 1 auto", fontWeight: 400 }}
                >
                    {title}
                </Typography>
                {actions}
            </Box>
            {subtitle && (
                <Typography
                    variant="h6"
                    color="text.secondary"
                    paragraph
                    sx={{ mt: 1, fontWeight: 400 }}
                >
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
}
